import { useQuery } from "react-query";
import DialogFullScreen from "../../../../../../components/DialogFullScreen";
import axiosClient from "../../../../../../lib/axios";
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery";
import { Box, BoxProps, Chip, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import LabelPrinter from "../../../../../../components/LabelPrinter";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import moment from "moment";
import DisplayContadores from "../../../../../../components/DisplayContadores";
import ValoresPagos from "./ValoresPagos";
import TitulosRelacionados from "./TitulosRelacionados";
import { useMemo } from "react";
import CardDadosTitulo from "./CardDadosTitulo";
import tratativaDeDatas from "../../../../../../helpers/tratativaDeDatas";
import { IDetalharTituloPagar } from "../../../../../../types/api/financeiro/detalhar-titulo-pagar";
import CardDadosPagamento from "./CardDadosPagamento";
import RateioDetalhe from "./RateioDetalhe";
import RetencaoDetalhe from "./RetencaoDetalhe";
import TitulosCompensados from "./TitulosCompensados";
import { ModalDetalheTituloProvider } from "../../../MIContasReceber/ModalDetalheTituloContext";

export default function DialogDetalheTitulo({
    open,
    value,
    handleClose
}: DialogDetalheTituloProps) {

    //querys e mutations
    const { data, isLoading } = useQuery({
        queryKey: ['financeiro/contas-pagar/detalhar-titulo-pagar', value],
        queryFn: () => axiosClient.get("/financeiro/contas-pagar/detalhar-titulo-pagar", {
            params: { id_titulo: value }
        }),
        cacheTime: 0
    });
    const detalhe: TDataQuery = data?.data || null
    //

    //funçoes manipuladoras
    //

    //valores importantes
    const boxStyle: BoxProps = { component: Paper, p: 2, display: "flex", flexDirection: "column", gap: 3 }

    const VencimentoLabel = useMemo(() => {
        if (!detalhe?.data_vencimento || Number(detalhe.saldo_titulo) === 0) return <></>

        const diasVencimento = moment(detalhe.data_vencimento, "YYYY-MM-DD").startOf('day').diff(moment().startOf('day'), 'days')

        let textoItems = { color: "", label: "" }

        if (diasVencimento < 0) {
            textoItems.color = "error"
            textoItems.label = `Vencido há ${Math.abs(diasVencimento)} dias(s)`
        }
        if (diasVencimento === 0) {
            textoItems.color = "warning.main"
            textoItems.label = `Vence hoje`
        }
        if (diasVencimento === 1) {
            textoItems.color = "info.main"
            textoItems.label = `Vence amanhã`
        }
        if (diasVencimento > 1) {
            textoItems.color = "info.main"
            textoItems.label = `Vence em ${diasVencimento} dias(s)`
        }

        return <Typography variant="caption" color={textoItems.color}>{textoItems.label}</Typography>
    }, [detalhe])
    //


    return (
        <DialogFullScreen
            {...{ open, handleClose }}
            title="Detalhe do título a pagar"
        >
            {isLoading && <LoadingApiQuery />}

            {
                (!isLoading && detalhe) &&
                <>
                    <Grid container spacing={2}>
                        <Grid container size={{xs: 12, md: 4}} spacing={2} alignSelf="flex-start">
                            <Grid size={{xs: 12}}>
                                <Box {...boxStyle}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <LabelPrinter
                                            titulo="Código"
                                            valor={detalhe.codigo}
                                        />

                                        {
                                            (detalhe.data_pagamento) ?
                                                <Chip label={Number(detalhe.saldo_titulo) === 0 ? "Pago" : "Parcialmente Pago"} color="success" variant="outlined" /> :
                                                <Chip label="Em aberto" color="warning" variant="outlined" />
                                        }
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <LabelPrinter
                                            titulo="Saldo do título"
                                            valor={formatarValorMonetario(detalhe.saldo_titulo)}
                                        />

                                        <Stack direction="column">
                                            <LabelPrinter
                                                titulo="Vencimento"
                                                valor={moment(detalhe.data_vencimento).format("DD/MM/YYYY")}
                                            />
                                            {VencimentoLabel}
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <LabelPrinter
                                            titulo="Emitido em"
                                            valor={moment(detalhe.data_emissao).format("DD/MM/YYYY")}
                                            pesoValorFonte={400}
                                            size="small"
                                        />

                                        <LabelPrinter
                                            titulo="Pago em"
                                            valor={tratativaDeDatas(detalhe.data_pagamento)}
                                            pesoValorFonte={400}
                                            size="small"
                                        />
                                    </Stack>

                                    <Box>
                                        <DisplayContadores
                                            titulo="Valor original"
                                            valor={formatarValorMonetario(detalhe.valor_pagar)}
                                        />

                                        <DisplayContadores
                                            titulo="Saldo principal"
                                            valor={formatarValorMonetario(detalhe.saldo_titulo)}
                                        />

                                        <DisplayContadores
                                            titulo="Juros"
                                            valor={formatarValorMonetario(detalhe.juros)}
                                        />

                                        <DisplayContadores
                                            titulo="Descontos"
                                            valor={formatarValorMonetario(detalhe.valor_desconto)}
                                        />
                                    </Box>

                                    <Divider />

                                    <Typography fontWeight="bold" variant="h6">Valores pagos</Typography>
                                    <ValoresPagos {...{ detalhe }} idTitulo={value} />
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <Box {...boxStyle}>
                                    <Typography fontWeight="bold" variant="h6">Titulos relacionados</Typography>
                                    <TitulosRelacionados codigo={value} relacionados={detalhe.titulos_relacionados} />
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <ModalDetalheTituloProvider>
                                    <TitulosCompensados codigo={value} titulos={detalhe.titulos_receber_compensados} />
                                </ModalDetalheTituloProvider>
                            </Grid>
                        </Grid>

                        <Grid container size={{xs: 12, md: 8}} spacing={2} alignSelf="flex-start">
                            <Grid size={{xs: 12}}>
                                <CardDadosTitulo
                                    {...{ boxStyle, detalhe }}
                                    codigo={value}
                                />
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <CardDadosPagamento
                                    {...{ boxStyle, detalhe }}
                                    codigo={value}
                                />
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <RateioDetalhe
                                    {...{ boxStyle }}
                                    codigo={value}
                                    valores={detalhe.rateios}
                                    valorTotal={Number(detalhe.saldo_titulo)}
                                />
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <RetencaoDetalhe
                                    {...{ boxStyle }}
                                    codigo={value}
                                    valores={detalhe.retencoes}
                                    valorTotal={Number(detalhe.saldo_titulo)}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            }

        </DialogFullScreen>
    )
}

export const DadosTitulosItems = ({
    titulo,
    valor
}: DadosTitulosItemsProps) => {
    return (
        <Stack>
            <Typography fontWeight="bold" variant="subtitle2">{titulo}</Typography>
            <Typography variant="subtitle2">{valor}</Typography>
        </Stack>
    )
}

interface DialogDetalheTituloProps {
    open: boolean,
    value: string,
    handleClose: () => void
}

interface DadosTitulosItemsProps {
    titulo: string,
    valor: string
}

type TDataQuery = IDetalharTituloPagar | null